import React, { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { SaveButton, useNotify, useRefresh } from 'react-admin';
import { makeStyles } from '@mui/styles';

import { AdminOptionCats, CreditRisk, CreditRiskState, CreditStatus } from '@types';
import { ConfirmDelete, InitialCard, InitialRow, ScoreWidget, SelectInput } from '@components';
import { riskAdminHttp } from '@network/risk-admin-http';
import { creditStatusOptions } from '@pages/credit/CreditStatusField';
import { ExportRisk } from '@pages/credit-risk/export';
import { useRbacEdit } from '@hooks';
import { adminSettingsHttp } from '@network';


interface Option {
  id: number;
  name: string;
}

const statusOptions = creditStatusOptions
  .filter(o => ['moderation', 'bank_revision', 'approved', 'denied'].includes(o.id));
// const rejectReason = [
//   { id: 'Default behavior', name: 'Default behavior' },
//   { id: 'Overindebtedness', name: 'Overindebtedness' },
//   { id: 'Tax Strategy', name: 'Tax Strategy' },
//   { id: 'Cancelled Invoices', name: 'Cancelled Invoices' },
//   { id: 'No Retention', name: 'No Retention' },
//   { id: 'Less than one invoiced cycle', name: 'Less than one invoiced cycle' },
//   { id: 'Multiple clients', name: 'Multiple clients' },
//   { id: 'Low sales', name: 'Low sales' },
//   { id: 'Conditions not accepted', name: 'Conditions not accepted' },
//   { id: 'Unauthorized off taker', name: 'Unauthorized off taker' },
//   { id: 'Does not comply with policies', name: 'Does not comply with policies' },
// ];
const resetStatuses = [CreditStatus.BANK_REVISION, CreditStatus.APPROVED];

const ResetButton = ({ label, riskId, disabled, ...props }: any) => {
  const cls = useStyles();
  const refresh = useRefresh();
  const notify = useNotify();

  const submit = async () => {
    try {
      await riskAdminHttp.resetAnalysis(riskId);
      refresh();
      notify('Analysis has been restarted.');
    } catch (e) {
      console.error(e);
      notify('Failed to restart analysis. Please try again.', { type: 'error' });
    }
  };

  return (
    <ConfirmDelete title="Are you sure?" disabled={disabled} cb={submit}>
      <button className={cls.reset} type="button" disabled={disabled}>
        <svg className={cls.resetIcon} width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.8403 11.6266C19.7269 16.8223 14.7046 20.1566 9.62976 19.0691L8.7104 18.8721L9.11381 16.9896L10.0332 17.1866C14.6106 18.2167 19.1753 14.7745 19.3468 9.71163C19.4509 6.62297 17.6597 3.71884 14.8928 2.49586C10.3628 0.481562 5.37669 3.24276 4.32221 7.93924C4.21127 8.45693 4.13913 8.97309 4.13134 9.50305L4.84877 8.62307C5.17543 8.22052 5.76536 8.15988 6.15824 8.48035L6.17663 8.48429C6.57467 8.82555 6.63954 9.4203 6.31086 9.83227L4.24938 12.4523C4.0133 12.7463 3.63997 12.873 3.28344 12.7868C3.13635 12.7552 3.00651 12.688 2.89192 12.5946L0.345371 10.5229C-0.0546865 10.1911 -0.117536 9.5869 0.211137 9.17493C0.53981 8.76296 1.13176 8.69291 1.53182 9.02476L2.24291 9.61031C2.30482 3.66743 7.62478 -1.01088 13.6371 0.188888C15.7209 0.605886 17.627 1.76254 18.9494 3.4439C20.8475 5.84916 21.4445 8.85179 20.8331 11.6152L20.8403 11.6266Z" fill="white"/>
        </svg>
        <div className={cls.resetLabel}>Restart</div>
      </button>
    </ConfirmDelete>
  );
};

export const RiskProgressScore = ({ risk, readonly }: {risk: CreditRisk; readonly?: boolean}) => {
  const score = risk.riskScore || 0;
  const cls = useStyles();
  const [isReportOpened, setReportOpened] = useState(false);
  const [reasons, setReasons] = useState<Option[]>([]);
  const openReport = risk ? () => setReportOpened(true) : undefined;
  const creditStatus = useWatch({ name: 'creditStatus' });
  const rbacEdit = useRbacEdit();

  const isDenied = risk?.credit?.isDenied;

  const isResetAvailable = !readonly
    && risk.state === CreditRiskState.COMPLETE
    && resetStatuses.includes(risk.credit.status);

  useEffect(() => {
    (async () => {
      const [allReasons] = await Promise.all([
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.SALES_CREDITS_RISK_REJECTION_REASON),
      ]);
      setReasons(allReasons.map(i => ({ id: i.id as number, name: i.name })));
    })();
  }, []);

  return (
    <>
      <InitialCard left="Score" autoHeight mediumPadding>
        {rbacEdit ? (
          <div className={cls.headActions}>
            <ResetButton
              className={cls.actionTopSave}
              label="" riskId={risk.id}
              type="button"
              disabled={!isResetAvailable}
            />
            <SaveButton className={cls.actionTopSave} label="" type="button" disabled={readonly} />
          </div>
        ) : null}
        <div className={cls.box}>
          <ScoreWidget
            value={score}
            percents={score > risk.successPercent ? 90 : 10}
            onClick={openReport}
          />
          <InitialRow title="Status" className={cls.statusRow}>
            <SelectInput
              label={false}
              source="creditStatus"
              choices={statusOptions}
              required
              disabled={readonly || isDenied}
            />
          </InitialRow>
          <InitialRow title="Reason" className={cls.reasonRow}>
            <SelectInput
              label={false}
              source="creditRejectReasonId"
              disabled={creditStatus !== 'denied'}
              required={isDenied}
              choices={reasons}
            />
          </InitialRow>
        </div>
      </InitialCard>
      <ExportRisk
        creditId={risk.creditId}
        isOpened={isReportOpened}
        setOpened={setReportOpened}
      />
    </>
  );
};

const useStyles = makeStyles({
  box: {
    position: 'relative',
    zIndex: 100,
    marginTop: 12,
  },
  headActions: {
    position: 'absolute',
    top: 6,
    right: 16,
    zIndex: 200,
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  statusRow: {
    marginLeft: '15%',
    marginRight: '33%',
    marginTop: 28,
  },
  reasonRow: {
    marginLeft: '15%',
    marginRight: '33%',
    marginTop: 28,
  },
  actionTopSave: {
    color: '#FFF !important',
    background: '#042E6B !important',
    minWidth: 38,
    height: 38,
    textTransform: 'none',
    borderRadius: 8,
    zIndex: 200,
    padding: 0,
    cursor: 'pointer',

    '&:disabled': {
      background: 'grey !important',
      cursor: 'not-allowed',
    },

    '& svg': {
      marginRight: '0 !important',
    },
  },
  reset: {
    appearance: 'none',
    borderRadius: 8,
    minWidth: 38,
    height: 38,
    background: '#042E6B',
    border: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    color: '#FFF',
    zIndex: 200,

    '&:disabled': {
      background: 'grey !important',
      cursor: 'not-allowed',
    },

    '& button': {
      appearance: 'none',
      borderRadius: 8,
      minWidth: 38,
      height: 38,
      background: '#042E6B',
      border: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 0,
      margin: 0,
      color: '#FFF',
      zIndex: 200,

      '&:disabled': {
        background: 'grey !important',
        cursor: 'not-allowed',
      },
    },
  },
  resetIcon: {
    marginTop: 2,
  },
  resetLabel: {
    fontSize: 9,
    color: '#FFF',
  },
});
